import {
	CheckboxField,
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FormikErrors } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { contactTypeService } from "../shared/api/contactType";
import { ContactTypeFormValues } from "../shared/types/contactType";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ContactTypeFormValues) => {
		const errors: FormikErrors<ContactTypeFormValues> = {};
		return {
			...errors,
			...validateEachLanguage<ContactTypeFormValues["translations"][number]>(values.translations, (_, translation) => {
				const errors: Record<string, any> = {};
				if (!translation.name) {
					errors.name = t("Field is required");
				}
				return errors;
			}),
		};
	};
};

const emptyTranslation = { name: "" };

const ContactTypeFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<CheckboxField name="isDefault" label={t("Mark as default")} />
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const ContactTypeCreateDrawer: FC<CreateDrawerFormProps<ContactTypeFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();
	const queryClient = useQueryClient();

	const initialValues: ContactTypeFormValues = useMemo(
		() => ({
			id: -1,
			isDefault: false,
			translations: [{ locale: languageSettings.fallback, name: "" }],
		}),
		[languageSettings.fallback],
	);

	const handleSubmit = async (values: ContactTypeFormValues) => {
		const result = await props.onSubmit(values);
		contactTypeService.invalidateAll(queryClient);
		return result;
	};

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create contact type")}
			validate={validate}
			onSubmit={handleSubmit}
			renderForm={(formProps) => <ContactTypeFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

export const ContactTypeUpdateDrawer: FC<UpdateDrawerFormProps<ContactTypeFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const queryClient = useQueryClient();

	const handleSubmit = async (values: ContactTypeFormValues) => {
		const result = await props.onSubmit(values);
		contactTypeService.invalidateAll(queryClient);
		return result;
	};

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit contact type")}
			queryFn={() => contactTypeService.show(props.id).then((x) => x.data.data)}
			queryKey={contactTypeService.showKey(props.id)}
			validate={validate}
			onSubmit={handleSubmit}
			renderSuccess={(formProps) => <ContactTypeFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
